/* global I18n */
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { ProductContext } from '../../../contexts/ProductContext'
import { FileContext } from '../../../contexts/FileContext'
import { CUSTOMER, NATIVE, PRINTABLE_CERTIFICATION } from '../../products/constants'
import { selectLicense } from '../../../products/react_components/selectors/formSelectors'
import { licenseValueMapper } from '../../../utils/SaveValidations'

const i18nOptions = {
  scope: 'turbosquid.products.printable_certification_panel',
}

const isDownloadableStl = (file) => (
  file.type === CUSTOMER && /\.stl$/i.test(file.attributes.name)
)
const solidWorksFormats = ['1011', '1012', 'solidworks_part', 'solidworks_assembly']
const isSolidWorksFile = (file) => (
  file.type === NATIVE && solidWorksFormats.includes(String(file.attributes.format))
)

const PrintableCertificationPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [bodyMaxHeight, setBodyMaxHeight] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [pendingChecked, setPendingChecked] = useState(null)
  const [saveError, setSaveError] = useState(false)
  const [removalFailed, setRemovalFailed] = useState(false)
  const license = useSelector(selectLicense)
  const persistedLicense = useSelector(({ formState }) => formState.persistedLicense)
  const {
    product,
    certifications: {
      certifications,
      onAddCertification,
      onRemoveCertification,
    },
  } = useContext(ProductContext)
  const {
    files: { associated, native }, deletedFiles = [], pendingUndoDelete = false,
  } = useContext(FileContext)
  const printableCertification = certifications[PRINTABLE_CERTIFICATION]
  const initialFilesLoaded = Array.isArray(product?.files)
  const printableFile = Object.values(associated).find(isDownloadableStl)
    || Object.values(native).find(isSolidWorksFile)
  const hasPrintableFile = Boolean(printableFile)
  const hasEligibleLicense = license === licenseValueMapper.royalty_free_all_extended_uses
  const hasPendingPrintableDeletion = deletedFiles.some((file) => (
    isDownloadableStl(file) || isSolidWorksFile(file)
  ))
  const wasQualifyingFilePendingUndo = useRef(false)
  const hasCommittedPrintableFile = hasPrintableFile || hasPendingPrintableDeletion
    || (pendingUndoDelete && wasQualifyingFilePendingUndo.current)
  const hasPersistedEligibleLicense = (
    persistedLicense === licenseValueMapper.royalty_free_all_extended_uses
  )
  const panelRef = useRef(null)
  const isRemovalInFlight = useRef(false)
  const previousEligibility = useRef({ hasCommittedPrintableFile, hasPersistedEligibleLicense })
  const isCertified = printableCertification?.status === 'certified'
  const isSelected = Boolean(printableCertification)
  const displayChecked = pendingChecked === null ? isSelected : pendingChecked
  const canAttest = hasPrintableFile && hasEligibleLicense && hasPersistedEligibleLicense
  const isDisabled = !canAttest || isSaving || isCertified
  const canRetryRemoval = removalFailed && isSelected
    && (!hasCommittedPrintableFile || !hasPersistedEligibleLicense)
  let status = 'needs_attention'
  if (canAttest) status = 'ready'
  if (isSelected) status = 'selected'
  if (isSaving) status = 'saving'
  if (isCertified) status = 'certified'
  let hintKey = 'ineligible_hint'
  if (canAttest) hintKey = 'eligible_hint'
  if (hasEligibleLicense && !hasPersistedEligibleLicense) hintKey = 'save_license_hint'
  if (isCertified) hintKey = 'certified_hint'
  let confirmationStateKey = 'confirm_below'
  if (isSelected) confirmationStateKey = 'confirmed'
  if (isSaving) confirmationStateKey = 'status.saving'

  useLayoutEffect(() => {
    if (!isExpanded) return undefined

    const updateBodyMaxHeight = () => {
      const panelBottom = panelRef.current.getBoundingClientRect().bottom
      setBodyMaxHeight(Math.max(0, window.innerHeight - panelBottom - 12))
    }
    updateBodyMaxHeight()
    window.addEventListener('resize', updateBodyMaxHeight)
    return () => window.removeEventListener('resize', updateBodyMaxHeight)
  }, [isExpanded])

  useEffect(() => {
    if (!isExpanded) return undefined

    const closeOnOutsideClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setIsExpanded(false)
    }
    document.addEventListener('click', closeOnOutsideClick)
    return () => document.removeEventListener('click', closeOnOutsideClick)
  }, [isExpanded])

  useEffect(() => {
    if (hasPendingPrintableDeletion) wasQualifyingFilePendingUndo.current = true
    else if (!pendingUndoDelete) wasQualifyingFilePendingUndo.current = false
  }, [hasPendingPrintableDeletion, pendingUndoDelete])

  useEffect(() => {
    const lostEligibility = (previousEligibility.current.hasCommittedPrintableFile
      && !hasCommittedPrintableFile)
      || (previousEligibility.current.hasPersistedEligibleLicense && !hasPersistedEligibleLicense)
    previousEligibility.current = { hasCommittedPrintableFile, hasPersistedEligibleLicense }

    if ((hasCommittedPrintableFile && hasPersistedEligibleLicense)
      || !isSelected || (isCertified && !lostEligibility && !initialFilesLoaded)
      || isRemovalInFlight.current) return

    isRemovalInFlight.current = true
    setSaveError(false)
    setRemovalFailed(false)
    onRemoveCertification(PRINTABLE_CERTIFICATION)
      .catch((error) => {
        console.error(error)
        setSaveError(true)
        setRemovalFailed(true)
        setIsExpanded(true)
      })
      .finally(() => {
        isRemovalInFlight.current = false
      })
  }, [
    hasCommittedPrintableFile, hasPersistedEligibleLicense, isSelected, isCertified,
    onRemoveCertification, initialFilesLoaded,
  ])

  const handleRetryRemoval = async () => {
    if (!canRetryRemoval || isRemovalInFlight.current) return

    isRemovalInFlight.current = true
    setSaveError(false)
    setRemovalFailed(false)
    setIsSaving(true)
    try {
      await onRemoveCertification(PRINTABLE_CERTIFICATION)
    } catch (error) {
      console.error(error)
      setSaveError(true)
      setRemovalFailed(true)
    } finally {
      isRemovalInFlight.current = false
      setIsSaving(false)
    }
  }

  const handleChange = async () => {
    if (isSelected && isRemovalInFlight.current) return
    if (!isSelected && !canAttest) return

    setSaveError(false)
    setPendingChecked(!isSelected)
    setIsSaving(true)
    if (isSelected) isRemovalInFlight.current = true

    try {
      if (isSelected) {
        await onRemoveCertification(PRINTABLE_CERTIFICATION)
      } else {
        await onAddCertification(PRINTABLE_CERTIFICATION)
      }
    } catch (error) {
      console.error(error)
      setSaveError(true)
    } finally {
      if (isSelected) isRemovalInFlight.current = false
      setPendingChecked(null)
      setIsSaving(false)
    }
  }

  return (
    <section
      className={`printable-certification-container ${isCertified ? 'certified' : ''}`}
      aria-labelledby="printable-certification-title"
      ref={panelRef}
    >
      <button
        className="printable-certification-heading"
        type="button"
        aria-expanded={isExpanded}
        aria-controls="printable-certification-body"
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <span className="printable-certification-heading-copy">
          <span className="printable-certification-eyebrow">{I18n.t('eyebrow', i18nOptions)}</span>
          <span className="printable-certification-title" id="printable-certification-title">{I18n.t('title', i18nOptions)}</span>
          <span className="printable-certification-description">{I18n.t('description', i18nOptions)}</span>
        </span>
        <span className={`printable-certification-status ${status}`} role="status">
          {I18n.t(`status.${status}`, i18nOptions)}
        </span>
        <span className="printable-certification-chevron" aria-hidden="true" />
      </button>
      <div
        className="printable-certification-body"
        id="printable-certification-body"
        hidden={!isExpanded}
        style={isExpanded ? { maxHeight: bodyMaxHeight } : undefined}
      >
        <h4>{I18n.t('requirements', i18nOptions)}</h4>
        <ul className="printable-certification-requirements">
          <li>
            <span className={`requirement-mark ${hasEligibleLicense ? 'complete' : 'needed'}`} aria-hidden="true">{hasEligibleLicense ? '✓' : '!'}</span>
            <span className="requirement-copy">
              <strong>{I18n.t('license', i18nOptions)}</strong>
              <small>{I18n.t(hasEligibleLicense ? 'license_selected' : 'license_missing', i18nOptions)}</small>
            </span>
            <span className="requirement-state">{I18n.t(hasEligibleLicense ? 'complete' : 'needed', i18nOptions)}</span>
          </li>
          <li>
            <span className={`requirement-mark ${hasPrintableFile ? 'complete' : 'needed'}`} aria-hidden="true">{hasPrintableFile ? '✓' : '!'}</span>
            <span className="requirement-copy">
              <strong>{I18n.t('file', i18nOptions)}</strong>
              <small>{printableFile ? I18n.t('file_detected', { ...i18nOptions, name: printableFile.attributes.name }) : I18n.t('file_missing', i18nOptions)}</small>
            </span>
            <span className="requirement-state">{I18n.t(hasPrintableFile ? 'complete' : 'needed', i18nOptions)}</span>
          </li>
          <li>
            <span className={`requirement-mark ${displayChecked ? 'complete' : 'confirmation'}`} aria-hidden="true">{displayChecked ? '✓' : '○'}</span>
            <span className="requirement-copy">
              <strong>{I18n.t('print_ready', i18nOptions)}</strong>
              <small>{I18n.t('print_ready_detail', i18nOptions)}</small>
            </span>
            <span className="requirement-state">{I18n.t(confirmationStateKey, i18nOptions)}</span>
          </li>
        </ul>
        {!hasPrintableFile && <p className="printable-certification-next-step">{I18n.t('file_next_step', i18nOptions)}</p>}
        {hasPrintableFile && !hasEligibleLicense && <p className="printable-certification-next-step">{I18n.t('license_next_step', i18nOptions)}</p>}
        <label className={`printable-certification-attestation ${isDisabled ? 'disabled' : ''}`} htmlFor="addPrintableCertification">
          <input
            id="addPrintableCertification"
            name="addPrintableCertification"
            aria-label={I18n.t('attestation', i18nOptions)}
            aria-describedby="printable-certification-hint"
            onChange={handleChange}
            type="checkbox"
            value={PRINTABLE_CERTIFICATION}
            checked={displayChecked}
            disabled={isDisabled}
          />
          <span>
            {I18n.t('attestation', i18nOptions)}
            <small id="printable-certification-hint">{I18n.t(hintKey, i18nOptions)}</small>
          </span>
        </label>
        {saveError && !removalFailed && <p className="printable-certification-error" role="alert">{I18n.t('save_error', i18nOptions)}</p>}
        {canRetryRemoval && (
          <p className="printable-certification-error" role="alert">
            {I18n.t('remove_error', i18nOptions)}
            {' '}
            <button type="button" className="printable-certification-retry" onClick={handleRetryRemoval} disabled={isSaving}>
              {I18n.t('retry_removal', i18nOptions)}
            </button>
          </p>
        )}
        <details className="printable-certification-help">
          <summary>{I18n.t('help_title', i18nOptions)}</summary>
          <p>{I18n.t('help_body', i18nOptions)}</p>
        </details>
      </div>
    </section>
  )
}

const PrintableCertificationForm = ({ enabled }) => (
  enabled ? <PrintableCertificationPanel /> : null
)

PrintableCertificationForm.propTypes = {
  enabled: PropTypes.bool,
}

PrintableCertificationForm.defaultProps = {
  enabled: false,
}

export default PrintableCertificationForm
